import { CardCheckbox, ELinxConnectionMethod, ElinxRequestId, MultiSelect, TextArea, TextField, Tooltip } from 'vopay';
import type { FieldData } from 'vopay';
import SelectOptionLocked from '../SelectOptionLocked/SelectOptionLocked';

import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

type AccountSelectionMethod = 'online' | 'manual';

export type FormValues = {
  [ElinxRequestId.ReceiverName]: string;
  [ElinxRequestId.SenderName]: string;
  [ElinxRequestId.PaymentSelectionMethod]: ELinxConnectionMethod[];
  [ElinxRequestId.AccountSelectionMethod]: AccountSelectionMethod[];
  [ElinxRequestId.Note]: string;
  [ElinxRequestId.InvoiceNumber]: string;
  [ElinxRequestId.SendEmail]: boolean;
};

export type SerializedFormValues = Omit<
  FormValues,
  ElinxRequestId.PaymentSelectionMethod | ElinxRequestId.AccountSelectionMethod
> & {
  [ElinxRequestId.PaymentSelectionMethod]: string;
  [ElinxRequestId.AccountSelectionMethod]: string;
};

export const buildInitialValues = (receiverName: string, senderName: string): FormValues => {
  return {
    ReceiverName: receiverName,
    SenderName: senderName,
    PaymentSelectionMethod: [],
    AccountSelectionMethod: [],
    InvoiceNumber: '',
    Note: '',
    SendEmail: false,
  };
};

export const useElinxRequestNameRows = (): FieldData<FormValues>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.ElinxRequestDialog' });

  return [
    {
      label: () => t('recipientName', { defaultValue: 'Recipient Name' }),
      fieldName: ElinxRequestId.ReceiverName,
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" readOnly {...getFormikData<string>({})} />;
      },
      validate: () =>
        Yup.string()
          .label(t('recipientName', { defaultValue: 'Recipient Name' }))
          .required(),
    },
    {
      label: () => t('senderName', { defaultValue: 'Sender Name' }),
      fieldName: ElinxRequestId.SenderName,
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<string>({})} />;
      },
      validate: () =>
        Yup.string()
          .label(t('senderName', { defaultValue: 'Sender Name' }))
          .required(),
    },
  ];
};

type MethodRowsOptions = {
  // Host facts the portal read from its ClientData/LockedFeatures providers.
  country: string;
  lockedSelectOptions?: { [featureKey: string]: string };
  onLockedFeature?: (featureKey: string) => void;
};

export const useElinxRequestMethodRows = ({
  country,
  lockedSelectOptions,
  onLockedFeature,
}: MethodRowsOptions): FieldData<FormValues>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.ElinxRequestDialog' });

  const paymentSelectionMethods: {
    label: string;
    value: ELinxConnectionMethod;
    featureKey: string;
    visible: boolean;
  }[] = [
    {
      label: t('bankAccount', { defaultValue: 'Bank Account' }),
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      label: t('email', { defaultValue: 'Email' }),
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA',
    },
    {
      label: t('creditCard', { defaultValue: 'Credit Card' }),
      value: 'credit',
      featureKey: 'Credit Card',
      visible: true,
    },
    {
      label: t('debitCard', { defaultValue: 'Debit Card' }),
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: true,
    },
    {
      label: t('paypal', { defaultValue: 'PayPal' }),
      value: 'paypal',
      featureKey: 'PayPal',
      visible: true,
    },
    {
      label: t('venmo', { defaultValue: 'Venmo' }),
      value: 'venmo',
      featureKey: 'Venmo',
      visible: country === 'US',
    },
    {
      label: t('zelle', { defaultValue: 'Zelle' }),
      value: 'zelle',
      featureKey: 'Zelle',
      visible: country === 'US',
    },
  ];

  const accountSelectionMethods: { label: string; value: AccountSelectionMethod }[] = [
    {
      label: t('online', { defaultValue: 'Online' }),
      value: 'online',
    },
    {
      label: t('manual', { defaultValue: 'Manual' }),
      value: 'manual',
    },
  ];

  return [
    {
      label: () => t('paymentSelectionMethod', { defaultValue: 'Payment Selection Method' }),
      fieldName: ElinxRequestId.PaymentSelectionMethod,
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values[ElinxRequestId.PaymentSelectionMethod].join(','),
      renderField: ({ ctx, getFormikData }) => {
        return (
          <MultiSelect
            sort={false}
            options={paymentSelectionMethods.filter((option) => option.visible !== false)}
            {...getFormikData({
              onChange: (e, args?: { options: (typeof paymentSelectionMethods)[number][] }) => {
                const values = (args?.options ?? []).map(({ value }) => value);
                ctx.setFieldValue(e.target.name, values);

                if (!values.includes('bank')) {
                  ctx.setFieldValue(ElinxRequestId.AccountSelectionMethod, []);
                }
              },
            })}
            renderOption={(option, args) => (
              <SelectOptionLocked
                {...args}
                featureKey={option.featureKey}
                lockedSelectOptions={lockedSelectOptions}
                onLockedFeature={onLockedFeature}
              >
                {option.label}
              </SelectOptionLocked>
            )}
          />
        );
      },
      validate: () =>
        Yup.array()
          .label(t('paymentSelectionMethod', { defaultValue: 'Payment Selection Method' }))
          .min(1, t('Validation.atLeastOne', { defaultValue: 'Select at least one option' }))
          .required(),
    },
    {
      label: () => t('accountSelectionMethod', { defaultValue: 'Account Connection Method' }),
      fieldName: ElinxRequestId.AccountSelectionMethod,
      getInputState: (ctx) =>
        ctx.values[ElinxRequestId.PaymentSelectionMethod].includes('bank') ? 'required' : 'disabled',
      showField: () => true,
      serializeField: (values) => values[ElinxRequestId.AccountSelectionMethod].join(','),
      tooltip: () => (
        <Tooltip
          information={t('accountSelectionMethodTooltip', { defaultValue: 'Only applicable for bank accounts.' })}
          position="top"
        />
      ),
      renderField: ({ ctx, getFormikData }) => {
        return (
          <MultiSelect
            options={accountSelectionMethods}
            {...getFormikData({
              onChange: (e, args?: { options: (typeof accountSelectionMethods)[number][] }) => {
                ctx.setFieldValue(
                  e.target.name,
                  (args?.options ?? []).map(({ value }) => value),
                );
              },
            })}
          />
        );
      },
      validate: (ctx) =>
        Yup.array().when(ElinxRequestId.PaymentSelectionMethod, {
          is: () => ctx.values[ElinxRequestId.PaymentSelectionMethod].includes('bank'),
          then: Yup.array()
            .label(t('accountSelectionMethod', { defaultValue: 'Account Connection Method' }))
            .min(1, t('Validation.atLeastOne', { defaultValue: 'Select at least one option' }))
            .required(),
        }),
    },
  ];
};

export const useElinxRequestDetailRows = (): FieldData<FormValues>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.ElinxRequestDialog' });

  return [
    {
      label: () => t('note', { defaultValue: 'Message to Contact' }),
      fieldName: ElinxRequestId.Note,
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <TextArea
            variant="lined"
            placeholder={t('notePlaceholder', { defaultValue: 'Enter message here...' })}
            {...getFormikData<string>({})}
          />
        );
      },
    },
    {
      label: () => t('invoiceNumber', { defaultValue: 'Invoice Number' }),
      fieldName: ElinxRequestId.InvoiceNumber,
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<string>({})} />;
      },
    },
    {
      fieldName: ElinxRequestId.SendEmail,
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        return (
          <CardCheckbox
            {...getFormikData<boolean>({})}
            label={t('sendEmail', { defaultValue: 'Send Email' })}
            renderLabelEnd={() => (
              <Tooltip
                information={t('sendEmailTooltip', {
                  defaultValue: 'Will overwrite eLinx notification settings for this transaction.',
                })}
                position="top"
              />
            )}
            labelPosition="right"
            checked={ctx.values[ElinxRequestId.SendEmail]}
          />
        );
      },
    },
  ];
};
